import { ReactElement, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Fab,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { toastr } from '../../services/toast-service';
import LazyImage from '../../components/LazyImage/LazyImage';
import LineAwesome from '../../components/LineAwesome/LineAwesome';
import { Loader } from '../../components/Loader/Loader';
import { StrapiProduct } from '../../core/models/strapi-product.model';
import { StrapiBookmark } from '../../core/models/strapi-bookmark.model';
import { StrapiResponse } from '../../core/models/response/strapi-response.model';
import { getPreviewImage } from '../../utils/imageUtils';
import { strapiClient } from '../../core/api/strapi-client';
import { StrapiEntity } from '../../constants/StrapiEntity';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { Logger } from '../../core/logger/Logger';
import './BookmarkCollection.scss';
import { GeneralHeading } from '../../components/MyAccount/Views/GeneralHeading';
import { WEBSHOP_ENABLED } from '../../constants/Settings';

// As on the product cards: without the webshop, or for a product on request,
// there is no price to show (such products are stored with 0).
const hasPrice = (product: StrapiProduct): boolean =>
  WEBSHOP_ENABLED && !product.requestOnly && !!product.price;

interface BookmarkCollectionProps {
  editMode?: boolean;
  onNavigate?: () => void;
}

export const BookmarkCollection = ({
  editMode = true,
  onNavigate
}: BookmarkCollectionProps): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useAtomValue(userAtom, { store: regionStore });

  const [loading, setLoading] = useState(true);
  const [editTitle, setEditTitle] = useState(false);
  const [addNewList, setAddNewList] = useState(false);
  const [newListTitle, setNewListTitle] = useState<string>(
    t('bookmarks.newListTitle', 'Neue Merkliste')
  );
  const [selectedCollectionId, setSelectedCollectionId] = useState(0);
  const [selectedCollectionTitle, setSelectedCollectionTitle] = useState('');
  const [deletingProductId, setDeletingProductId] = useState<number>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Default to the most recently created collection on mount.
  useEffect(() => {
    const currentUser = regionStore.get(userAtom);
    const collections = currentUser?.bookmarkCollections;
    if (collections?.length) {
      const last = collections[collections.length - 1];
      setSelectedCollectionId(last?.id ?? 0);
      setSelectedCollectionTitle(last?.title ?? '');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user && !loading) {
      history.push('/login');
    }
  }, [user, loading, history]);

  const handleCollectionChange = (event: SelectChangeEvent<number>) => {
    const id = Number(event.target.value);
    const collection = user?.bookmarkCollections?.find((c) => c.id === id);
    setSelectedCollectionId(id);
    setSelectedCollectionTitle(collection?.title ?? '');
    setEditTitle(false);
    setAddNewList(false);
  };

  const handleSaveList = () => {
    const currentUser = regionStore.get(userAtom);
    if (!currentUser?.id) {
      return;
    }

    setLoading(true);
    // The backend links the new list to the caller. Writing the whole list back
    // through `/users/me` would replace it with this tab's copy, unlinking a
    // list another tab created meanwhile.
    strapiClient
      .create(StrapiEntity.bookmarkCollections, { title: newListTitle })
      .then(async (response) => {
        const created = (response as unknown as { data: StrapiResponse<StrapiBookmark> }).data;
        // Refetch the fully-populated user so the store stays authoritative.
        await strapiClient.loadUser();
        toastr.success(
          t('successes.bookmarks.bindBookmarksCollectionToUser.title', 'Erfolg!'),
          t(
            'successes.bookmarks.bindBookmarksCollectionToUser.message',
            'Merkliste erfolgreich angelegt'
          )
        );
        setAddNewList(false);
        setNewListTitle('');
        setSelectedCollectionId(created.id);
        setSelectedCollectionTitle(created.attributes.title);
      })
      .catch((error) => {
        Logger.error(error);
        toastr.error(
          t('errors.error'),
          t('errors.bookmarks.createBookmarksCollection.message', 'Fehler beim Laden der Merkliste')
        );
      })
      .finally(() => setLoading(false));
  };

  const handleTitleSave = () => {
    if (!user || !selectedCollectionId) {
      return;
    }

    setLoading(true);
    strapiClient
      .update(StrapiEntity.bookmarkCollections, selectedCollectionId, {
        title: selectedCollectionTitle
      })
      .then(async () => {
        await strapiClient.loadUser();
        toastr.success(
          t('successes.bookmarks.updateBookmarkCollectionTitle.title', 'Erfolg'),
          t(
            'successes.bookmarks.updateBookmarkCollectionTitle.message',
            'Merkliste erfolgreich aktualisiert'
          )
        );
        setEditTitle(false);
      })
      .catch((error) => {
        Logger.error(error);
        toastr.error(
          t('errors.bookmarks.updateBookmarkCollection.title', 'Fehler!'),
          t(
            'errors.bookmarks.updateBookmarkCollection.message',
            'Merkliste kann nicht aktualisiert werden'
          )
        );
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!selectedCollectionId) {
      return;
    }

    setLoading(true);
    strapiClient
      .delete(selectedCollectionId, StrapiEntity.bookmarkCollections)
      .then(async () => {
        await strapiClient.loadUser();
        toastr.success(
          t('bookmarks.deleteSuccess', 'Merkliste erfolgreich gelöscht'),
          selectedCollectionTitle
        );
        // Fall back to the last remaining collection (or none) from the refreshed store.
        const remaining = regionStore.get(userAtom)?.bookmarkCollections ?? [];
        const nextCollection = remaining[remaining.length - 1];
        setSelectedCollectionId(nextCollection?.id ?? 0);
        setSelectedCollectionTitle(nextCollection?.title ?? '');
      })
      .catch((error) => {
        Logger.error(error);
        toastr.error(
          t('errors.bookmarks.deleteBookmarkCollection.title', 'Fehler!'),
          t(
            'errors.bookmarks.deleteBookmarkCollection.message',
            'Fehler beim Löschen der Merkliste'
          )
        );
      })
      .finally(() => {
        setLoading(false);
        setShowDeleteConfirmation(false);
      });
  };

  const removeProduct = (productId: number) => {
    if (!user || !selectedCollectionId) {
      return;
    }

    setDeletingProductId(productId);
    strapiClient
      .update(StrapiEntity.bookmarkCollections, selectedCollectionId, {
        products: { disconnect: [productId] }
      })
      .then(async () => {
        await strapiClient.loadUser();
        setDeletingProductId(undefined);
        toastr.success(
          t('successes.bookmarks.removeProductFromBookmarkCollection.title', 'Erfolg!'),
          t('successes.bookmarks.removeProductFromBookmarkCollection.message', {
            value: selectedCollectionTitle
          })
        );
      })
      .catch((error) => {
        setDeletingProductId(undefined);
        Logger.error(error);
        toastr.error(
          t('errors.bookmarks.removeProductFromBookmarkCollection.title', 'Fehler!'),
          t(
            'errors.bookmarks.removeProductFromBookmarkCollection.message',
            'Produkt kann nicht aus der Merkliste entfernt werden'
          )
        );
      });
  };

  const selectedCollection = user?.bookmarkCollections?.find(
    (collection) => collection.id === selectedCollectionId
  );

  if (!user && !loading) {
    return <></>;
  }

  return (
    <>
      <GeneralHeading
        initDone
        heading={t('bookmarks.title', 'Merklisten')}
        displayEmptySearch={false}
      />

      {loading ? (
        <Box className='bookmark-collection__loader'>
          <Loader text='' />
        </Box>
      ) : addNewList ? (
        <Box className='bookmark-collection__add-form'>
          <TextField
            id='inputTitle'
            size='small'
            fullWidth
            label={t('bookmarks.titleLabel', 'Titel')}
            value={newListTitle}
            disabled={loading}
            onChange={(e) => setNewListTitle(e.target.value)}
          />
          <Box className='bookmark-collection__form-actions'>
            <Button variant='contained' color='primary' onClick={handleSaveList} disabled={loading}>
              {t('bookmarks.create', 'Anlegen')}
            </Button>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => setAddNewList(false)}
              disabled={loading}>
              {t('bookmarks.cancel', 'Abbrechen')}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box className='bookmark-collection__controls'>
            <FormControl fullWidth size='small'>
              <Select
                value={selectedCollectionId}
                MenuProps={{ disablePortal: true }}
                onChange={handleCollectionChange}>
                {user?.bookmarkCollections?.map((item) => (
                  <MenuItem key={item?.id} value={item?.id}>
                    {`${item.title} (${item.products ? item.products.length : 0})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Fab
              size='small'
              color='primary'
              className='bookmark-collection__add-button'
              aria-label={t('bookmarks.create', 'Anlegen')}
              onClick={() => setAddNewList(true)}>
              <LineAwesome icon='plus' />
            </Fab>
          </Box>

          {editTitle ? (
            <Box className='bookmark-collection__edit-form'>
              <TextField
                size='small'
                fullWidth
                label={t('bookmarks.titleLabel', 'Titel')}
                value={selectedCollectionTitle}
                disabled={loading}
                onChange={(e) => setSelectedCollectionTitle(e.target.value)}
              />
              <Box className='bookmark-collection__form-actions'>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleTitleSave}
                  disabled={loading || !selectedCollectionTitle.trim()}>
                  {t('bookmarks.save', 'Speichern')}
                </Button>
                <Button
                  variant='outlined'
                  color='inherit'
                  onClick={() => setEditTitle(false)}
                  disabled={loading}>
                  {t('bookmarks.cancel', 'Abbrechen')}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {editMode && !!user?.bookmarkCollections?.length && (
                <Box className='bookmark-collection__actions'>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setEditTitle(true)}
                    disabled={loading}>
                    {t('bookmarks.editTitle', 'Titel bearbeiten')}
                  </Button>
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={() => setShowDeleteConfirmation(true)}
                    disabled={loading}>
                    {t('bookmarks.deleteList', 'Merkliste löschen')}
                  </Button>
                </Box>
              )}
              {showDeleteConfirmation && (
                <Box className='bookmark-collection__confirm'>
                  <span className='bookmark-collection__confirm-text'>
                    {t('bookmarks.confirmDelete', 'Diese Merkliste wirklich löschen?')}
                  </span>
                  <Button
                    variant='contained'
                    color='error'
                    onClick={handleDelete}
                    disabled={loading}>
                    {t('bookmarks.delete', 'Löschen')}
                  </Button>
                  <Button
                    variant='outlined'
                    color='inherit'
                    onClick={() => setShowDeleteConfirmation(false)}
                    disabled={loading}>
                    {t('bookmarks.no', 'Nein')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {!addNewList && !loading && (
        <Box className='bookmark-collection__products'>
          {!user?.bookmarkCollections?.length && (
            <div className='bookmark-collection__empty'>
              {t('bookmarks.emptyCollections', 'Du hast noch keine Merklisten')}
            </div>
          )}
          {!!user?.bookmarkCollections?.length &&
            (!selectedCollection?.products?.length ? (
              <div className='bookmark-collection__empty'>
                {t('bookmarks.emptyProducts', 'Du hast keine Produkte in dieser Merkliste')}
              </div>
            ) : (
              selectedCollection.products.map((item: StrapiProduct) =>
                deletingProductId === item?.id ? (
                  <Loader key={item?.id} text='' />
                ) : (
                  <Box key={item?.id} className='bookmark-collection__product'>
                    <Link
                      to={`/produkt/${item?.urlSegment}`}
                      className='bookmark-collection__product-image'
                      onClick={onNavigate}>
                      <LazyImage src={getPreviewImage(item)} />
                    </Link>
                    <Box className='bookmark-collection__product-info'>
                      <Link
                        to={`/produkt/${item?.urlSegment}`}
                        className='bookmark-collection__product-title'
                        onClick={onNavigate}>
                        {item.title}
                      </Link>
                      <span className='bookmark-collection__product-vendor'>
                        {item?.vendor?.title}
                      </span>
                    </Box>
                    {hasPrice(item) && (
                      <Box className='bookmark-collection__product-price'>
                        € {item.price.toFixed(2)}
                      </Box>
                    )}
                    <LineAwesome
                      icon='trash'
                      size='2x'
                      className='bookmark-collection__product-delete'
                      onClick={() => removeProduct(item.id)}
                    />
                  </Box>
                )
              )
            ))}
        </Box>
      )}
    </>
  );
};
